/**
 * Banner guest main screen login form (`main_screen_variant: banner_guest`).
 * Flat fields — scroll lives in `MainScreenBannerGuest` bottom sheet host.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, Alert } from 'react-native';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { useTranslation, layoutLtrStyle } from '../../../shared/i18n';
import { useThemeColors, type ThemeColors, appTheme } from '../../../shared/theme';
import { requestPasswordReset } from '../../../data/domains/profile';
import {
  signInWithPhonePassword,
  finalizeLoginSuccess,
  authenticateWithBiometrics,
  hasStoredCredentials,
  isDeviceBiometryReady,
  loadStoredCredentials,
  enrichBlockingSigninOtpParams,
  getAuthStore,
  persistSessionUser,
  setLoginJustHappened,
  type CompleteSessionOptions,
} from '../../../shared/auth';
import { getFcmDeviceTokenForAuth } from '../../../platform/notifications/fcmDeviceToken';
import { useOrganizationSettings, resolvePhoneMaxDigits } from '../../../data/domains/config';
import { Clickable } from '../../../components/common/Clickable';
import { Input } from '../../../components/common/Input';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { useAsyncWithLoading } from '../../../components/ui/hooks/useAsyncWithLoading';
import { useAuthSheets } from '../../../shared/auth/authSheetContext';
import { getDevLoginPasswordInitial, getDevLoginPhoneInitial } from '../../../exDev';

export interface SignInCredentialFormProps {
  onClose: () => void;
  onOpenSignUp: () => void;
  onForgotPassword: (phone: string, country_iso_code: string) => void;
  session_options?: CompleteSessionOptions;
}

const DEFAULT_DIALING_CODE = '965';
const DEFAULT_COUNTRY_ISO = 'KW';
const FIELD_SPACING = 24;
const BUTTON_HEIGHT = 52;
const BIOMETRIC_BUTTON_SIZE = 52;

function createSignInSheetStyles(c: ThemeColors) {
  return StyleSheet.create({
    formSurface: {
      borderTopLeftRadius: appTheme.borderRadius.lg,
      borderTopRightRadius: appTheme.borderRadius.lg,
      overflow: 'hidden',
      backgroundColor: c.surface,
    },
    content: {
      paddingBottom: appTheme.spacing.xl,
      paddingTop: appTheme.spacing.xl,
    },
    fieldGroup: {
      marginBottom: FIELD_SPACING,
    },
    inputContainer: {
      marginBottom: 0,
    },
    phoneRowLtr: {
      ...layoutLtrStyle,
      alignSelf: 'stretch',
      width: '100%',
    },
    phonePrefixWrap: {
      justifyContent: 'center',
      paddingRight: appTheme.spacing.sm,
      borderRightWidth: StyleSheet.hairlineWidth,
    },
    phonePrefixText: {
      fontSize: 16,
      lineHeight: 22,
      fontWeight: '600',
    },
    primaryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginTop: FIELD_SPACING,
      marginBottom: FIELD_SPACING,
    },
    primaryButtonWrap: {
      flex: 1,
    },
    primaryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      height: BUTTON_HEIGHT,
      borderRadius: appTheme.borderRadius.md,
    },
    primaryButtonText: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.onPrimary,
    },
    biometricButton: {
      width: BIOMETRIC_BUTTON_SIZE,
      height: BIOMETRIC_BUTTON_SIZE,
      borderRadius: appTheme.borderRadius.md,
      borderWidth: StyleSheet.hairlineWidth,
      alignItems: 'center',
      justifyContent: 'center',
    },
    forgotWrap: {
      alignSelf: 'center',
      paddingVertical: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.md,
      marginBottom: FIELD_SPACING,
      minHeight: 44,
      justifyContent: 'center',
    },
    forgotText: {
      ...appTheme.typography.bodyMedium,
    },
    loginErrorText: {
      ...appTheme.typography.bodySmall,
      marginTop: appTheme.spacing.xs,
    },
    footerRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 4,
      paddingTop: FIELD_SPACING,
      minHeight: 44,
    },
    footerHint: {
      ...appTheme.typography.bodySmall,
    },
    footerLink: {
      ...appTheme.typography.bodyMedium,
    },
  });
}

export function SignInCredentialForm({ onClose, onOpenSignUp, onForgotPassword, session_options }: SignInCredentialFormProps) {
  const { t } = useTranslation();
  const c = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createSignInSheetStyles(c), [c]);
  const { openOtp } = useAuthSheets();
  const { settings } = useOrganizationSettings();
  const dialing_code_num = settings.dialing_code;
  const country_iso_from_settings = settings.country_iso_code;

  const dialing_code = dialing_code_num != null && String(dialing_code_num).trim() !== '' ? String(dialing_code_num).trim() : DEFAULT_DIALING_CODE;
  const user_phone_allowed_digit = resolvePhoneMaxDigits(settings.allowedDigitForPhone, dialing_code);
  const country_iso_code = typeof country_iso_from_settings === 'string' ? country_iso_from_settings : DEFAULT_COUNTRY_ISO;

  const [phone, setPhone] = useState(getDevLoginPhoneInitial());
  const [password, setPassword] = useState(getDevLoginPasswordInitial());
  const [loading, runAsync] = useAsyncWithLoading();
  const [auth_error, setAuthError] = useState<string | null>(null);
  const [biometric_available, setBiometricAvailable] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const has_creds = await hasStoredCredentials();
        const hardware_ok = await isDeviceBiometryReady();
        setBiometricAvailable(has_creds && hardware_ok);
      } catch {
        setBiometricAvailable(false);
      }
    })();
  }, []);

  const on_phone_input = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    setPhone(cleaned.slice(0, user_phone_allowed_digit));
    if (auth_error) {
      setAuthError(null);
    }
  };

  const finish_login = async (login_phone: string, login_password: string) => {
    const device_token = await getFcmDeviceTokenForAuth();
    const r = await signInWithPhonePassword(login_phone, login_password, { device_token, skipStoreUpdate: true });
    if (!r.ok) {
      setAuthError(r.message || t('auth.loginFailed'));
      return;
    }
    if (r.unverified) {
      await persistSessionUser(r.user);
      getAuthStore().setUser(r.user);
      setLoginJustHappened();
      onClose();
      openOtp(
        enrichBlockingSigninOtpParams({
          phone: login_phone,
          countryCode: `+${dialing_code}`,
          redirectTo: 'MainTabs',
          loginData: { phone: login_phone, password: login_password },
        }),
      );
      return;
    }
    await finalizeLoginSuccess({
      user: r.user,
      onDismiss: onClose,
      session_options,
      login_phone,
      login_password,
      offer_biometric_save: true,
      t,
    });
  };

  const handle_login = async () => {
    if (!phone.trim() || !password.trim()) {
      setAuthError(t('auth.phoneAndPasswordRequired'));
      return;
    }
    setAuthError(null);
    await runAsync(async () => {
      await finish_login(phone.trim(), password);
    }).catch((error: unknown) => {
      setAuthError(error instanceof Error ? error.message : t('auth.unexpectedError'));
    });
  };

  const handle_biometric_login = async () => {
    setAuthError(null);
    await runAsync(async () => {
      const authenticated = await authenticateWithBiometrics({
        title: t('auth.biometricPromptTitle'),
        subtitle: t('auth.biometricPromptSubtitle'),
      });
      if (!authenticated) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableShort'));
        return;
      }
      const credentials = await loadStoredCredentials();
      if (!credentials) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableMessage'));
        setBiometricAvailable(false);
        return;
      }
      await finish_login(credentials.username, credentials.password);
    }).catch((error: unknown) => {
      setAuthError(error instanceof Error ? error.message : t('auth.unexpectedError'));
    });
  };

  const handle_forgot_password = async () => {
    if (!phone.trim()) {
      setAuthError(t('auth.enterPhoneFirst'));
      return;
    }
    setAuthError(null);
    await runAsync(async () => {
      const response = await requestPasswordReset(phone.trim());
      if (response.success === true) {
        onClose();
        onForgotPassword(phone.trim(), country_iso_code);
      } else {
        setAuthError(response.message || t('auth.failedToSendResetCode'));
      }
    }).catch((error: unknown) => {
      setAuthError(error instanceof Error ? error.message : t('auth.unexpectedError'));
    });
  };

  const accent = c.primary;

  return (
    <View style={styles.formSurface}>
      <View style={styles.content}>
      <View style={styles.fieldGroup}>
        <View style={styles.phoneRowLtr}>
          <Input
            value={phone}
            onChangeText={on_phone_input}
            placeholder={t('auth.enterMobileNumber')}
            keyboardType="phone-pad"
            maxLength={user_phone_allowed_digit}
            containerStyle={styles.inputContainer}
            textAlign="left"
            leftAccessory={
              <View style={[styles.phonePrefixWrap, { borderRightColor: c.borderLight }]}>
                <Text style={[styles.phonePrefixText, { color: c.text }]} {...(Platform.OS === 'android' ? { includeFontPadding: false } : {})}>
                  +{dialing_code}
                </Text>
              </View>
            }
          />
        </View>
      </View>

      <View style={styles.fieldGroup}>
        <Input
          value={password}
          onChangeText={text => {
            setPassword(text);
            if (auth_error) {
              setAuthError(null);
            }
          }}
          placeholder={t('auth.enterPasswordPlaceholder')}
          secureTextEntry
          containerStyle={styles.inputContainer}
        />
      </View>

      {auth_error ? (
        <Text style={[styles.loginErrorText, { color: c.error }]} accessibilityLiveRegion="polite">
          {auth_error}
        </Text>
      ) : null}

      <View style={styles.primaryRow}>
        <Clickable useNativePressable testID="signin-continue" onPress={handle_login} disabled={loading} style={styles.primaryButtonWrap} accessibilityLabel={t('auth.continue')} accessibilityRole="button">
          <View style={[styles.primaryButton, { backgroundColor: accent }]}>
            {loading ? <ButtonLoader size={18} color={c.onPrimary} /> : <Text style={styles.primaryButtonText}>{t('auth.continue')}</Text>}
          </View>
        </Clickable>
        {biometric_available ? (
          <Clickable useNativePressable onPress={handle_biometric_login} disabled={loading} accessibilityLabel={t('auth.loginWithBiometrics')} accessibilityRole="button">
            <View style={[styles.biometricButton, { borderColor: c.borderLight, backgroundColor: c.surface }]}>
              <SvgIcon name="fingerprint" size={22} color={accent} />
            </View>
          </Clickable>
        ) : null}
      </View>

      <Clickable useNativePressable testID="signin-forgot-password" onPress={handle_forgot_password} style={styles.forgotWrap} accessibilityLabel={t('auth.forgotPassword')} accessibilityRole="button">
        <Text style={[styles.forgotText, { color: accent }]}>{t('auth.forgotPassword')}</Text>
      </Clickable>

      <View style={styles.footerRow}>
        <Text style={[styles.footerHint, { color: c.textSecondary }]}>{t('auth.notAUser')}</Text>
        <Clickable useNativePressable testID="signin-create-account" onPress={onOpenSignUp} accessibilityLabel={t('auth.createAccount')} accessibilityRole="button">
          <Text style={[styles.footerLink, { color: accent }]}>{t('auth.createAccount')}</Text>
        </Clickable>
      </View>
      </View>
    </View>
  );
}
